import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, Card, Typography, Select, MenuItem, FormControl, InputLabel, 
  Stack, Chip, Alert, Button, List, ListItem, ListItemText, Divider 
} from '@mui/material';
import { 
  Analytics as AnalyticsIcon, 
  Search as SearchIcon, 
  TrendingUp as TrendingUpIcon, 
  TrendingDown as TrendingDownIcon,
  Speed as SpeedIcon,
  MenuBook as MenuBookIcon,
  GpsFixed as GpsFixedIcon,
  Cancel as CancelIcon,
  RocketLaunch as RocketIcon,
  CheckCircle as CheckIcon,
  List as ListIcon
} from '@mui/icons-material';

import useStudioStore from '../../state/studioStore';
import { API } from '../../lib/api';

export default function SliceCore() {
  const { currentRun, pushEvent, setWorkflowSliceAnalysis } = useStudioStore();

  const [runs, setRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState('');
  const [sliceData, setSliceData] = useState(null); // normalized: { slices: [] } or null
  const [selectedSlice, setSelectedSlice] = useState('');
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [runsLoading, setRunsLoading] = useState(false);
  const analysisInProgress = useRef(false);
  const runsLoadedRef = useRef(false);
  const lastAnalysisTime = useRef(0);

  const toDate = (value) => {
    if (!value) return null;
    if (typeof value === 'number' && Number.isFinite(value)) {
      const numeric = value > 1e12 ? value : value * 1000;
      const d = new Date(numeric);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const formatDateTime = (value) => {
    const date = toDate(value);
    return date ? date.toLocaleString() : 'Unknown run time';
  };

  const formatPercent = (value, digits = 1) => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return '—';
    }
    return (value * 100).toFixed(digits);
  };

  // Load runs on mount - load once only
  useEffect(() => {
    console.log('[SliceCore] mount');
    const loadRuns = async () => {
      // CRITICAL: Prevent multiple API calls with ref guard
      if (runsLoadedRef.current || runsLoading || runs.length > 0) {
        console.log('[SliceCore] Skipping runs load - already loaded/loading');
        return;
      }
      
      runsLoadedRef.current = true; // Mark as loaded immediately
      setRunsLoading(true);
      setError('');
      
      try {
        console.log('Slice: Loading proof runs from API...');

        const data = await API.proof.runs();
        console.log('Got', data.length, 'runs');
        
        // Filter to completed runs only
        const completedRuns = data.filter(run => 
          run.metrics && (run.metrics.status === 'completed' || run.metrics.total_cases > 0)
        );
        
        console.log('Completed runs:', completedRuns.length);
        setRuns(completedRuns);
        
        // Auto-select first run only if no current selection
        if (completedRuns.length > 0 && !selectedRunId) {
          const firstRunId = String(completedRuns[0].id);
          console.log('[SliceCore] Auto-selecting first run:', firstRunId);
          setSelectedRunId(firstRunId);
        } else if (completedRuns.length === 0) {
          setError('No completed runs found. Run a Live Trial first to generate data for slice analysis.');
        }
        
      } catch (err) {
        console.error('Slice runs loading failed:', err);
        setError('Failed to load runs: ' + err.message);
        runsLoadedRef.current = false; // Reset on error so it can retry
      } finally {
        setRunsLoading(false);
      }
    };
    
    loadRuns();
  }, []); // Load once on mount

  // React to current run changes - but only set selection, don't auto-analyze
  useEffect(() => {
    if (currentRun && runs.length > 0 && runs.find(r => r.id === currentRun.id)) {
      const newRunId = String(currentRun.id);
      // Only update if it's actually different to prevent unnecessary re-renders
      if (newRunId !== selectedRunId) {
        console.log('[SliceCore] Updating selectedRunId from currentRun:', newRunId);
        setSelectedRunId(newRunId);
      }
    }
  }, [currentRun, runs]); // Removed selectedRunId from dependency to prevent loops



  // Normalizer ensures consistent shape
  const normalizeSlicesPayload = (payload) => {
    try {
      if (!payload) return { slices: [] };
      if (payload && typeof payload === 'object' && Array.isArray(payload.slices)) {
        return { slices: payload.slices.filter(s => s && typeof s === 'object') };
      }
      if (Array.isArray(payload)) {
        return { slices: payload.filter(s => s && typeof s === 'object') };
      }
      const firstArrayKey = Object.keys(payload).find(k => Array.isArray(payload[k]));
      if (firstArrayKey) {
        console.warn('SliceCore normalize: using array at key', firstArrayKey);
        return { slices: payload[firstArrayKey].filter(s => s && typeof s === 'object') };
      }
      console.warn('SliceCore normalize: unrecognized payload shape', payload);
      return { slices: [] };
    } catch (e) {
      console.error('SliceCore normalize error', e);
      return { slices: [] };
    }
  };

  const lastGoodSlicesRef = useRef(null);

  const analyzeSlices = async (maybeRunId) => {
    console.log('[SliceCore] analyzeSlices invoked', { maybeRunId });
    
    // CRITICAL: Prevent duplicate calls within 2 seconds
    const now = Date.now();
    if (analysisInProgress.current || (now - lastAnalysisTime.current < 2000)) {
      console.log('[SliceCore] Skipping analysis - already in progress or called recently');
      return;
    }
    lastAnalysisTime.current = now;
    
    const candidate = (maybeRunId && typeof maybeRunId === 'string') ? maybeRunId : selectedRunId;
    const targetRunId = typeof candidate === 'string' ? candidate : '';
    if (!targetRunId) {
      setError('Please select a completed run');
      return;
    }

    // Basic UUID shape check before hitting API - more lenient
    const uuidLike = /^[0-9a-fA-F-]{8,}/;
    if (!uuidLike.test(targetRunId)) {
      console.warn('SliceCore analyzeSlices: invalid runId shape', targetRunId, typeof targetRunId);
      setError('Invalid run id format');
      return;
    }
    console.log('SliceCore analyzeSlices start', { targetRunId });

    analysisInProgress.current = true;
    setLoading(true);
    setError('');
    setSelectedSlice('');

    try {
      console.log('[SliceCore] Starting API calls for runId:', targetRunId);
      
      // Try slices first
      let rawSlices = null;
      try {
        console.log('[SliceCore] Calling API.proof.slices with runId:', targetRunId);
        rawSlices = await API.proof.slices(targetRunId);
        console.log('[SliceCore] fetched slices raw', rawSlices);
        console.log('[SliceCore] slices raw type:', typeof rawSlices, 'isArray:', Array.isArray(rawSlices));
        if (rawSlices && rawSlices.slices) {
          console.log('[SliceCore] slices array length:', rawSlices.slices.length);
        }
      } catch (sliceErr) {
        console.error('[SliceCore] Slices API failed:', sliceErr);
        console.error('[SliceCore] Error details:', sliceErr.message, sliceErr.status);
        setError(`Slices API failed: ${sliceErr.message || sliceErr}`);
        rawSlices = { slices: [] }; // Fallback empty
      }
      
      // Try cases second
      let rawCases = [];
      try {
        console.log('[SliceCore] Calling API.proof.cases with runId:', targetRunId);
        rawCases = await API.proof.cases(targetRunId);
        console.log('[SliceCore] fetched cases raw length', Array.isArray(rawCases) ? rawCases.length : 'non-array');
        console.log('[SliceCore] first few cases:', rawCases.slice(0, 3));
      } catch (casesErr) {
        console.error('[SliceCore] Cases API failed:', casesErr);
        console.error('[SliceCore] Cases error details:', casesErr.message, casesErr.status);
        rawCases = []; // Fallback empty
      }
      
      const normalized = normalizeSlicesPayload(rawSlices);
      console.log('[SliceCore] normalized slices:', normalized);
      console.log('[SliceCore] normalized slices count:', normalized.slices.length);
      
      setSliceData(normalized);
      setCases(Array.isArray(rawCases) ? rawCases : []);
      
      if (normalized.slices.length > 0) {
        lastGoodSlicesRef.current = normalized;
        console.log('[SliceCore] Success: Found', normalized.slices.length, 'slices');
      } else {
        console.log('[SliceCore] Warning: No slices found after normalization');
      }
      
      setWorkflowSliceAnalysis({ 
        runId: targetRunId, 
        slices: normalized, 
        cases: Array.isArray(rawCases) ? rawCases : [] 
      });
      
      // Success message
      pushEvent({
        type: 'slice-analysis',
        title: `Slice analysis completed: ${normalized.slices.length} slices found`,
        meta: { runId: targetRunId, sliceCount: normalized.slices.length }
      });
      
    } catch (err) {
      console.error('Slice analysis error:', err);
      setError('Failed to analyze slices: ' + (err.message || 'Unknown error'));
      if (lastGoodSlicesRef.current) {
        // keep last good data visible instead of blank
        setSliceData(lastGoodSlicesRef.current);
      }
    } finally {
      setLoading(false);
      analysisInProgress.current = false;
      console.log('[SliceCore] analyzeSlices finished');
    }
  };

  const getFilteredCases = (tag) => {
    if (!selectedSlice || !cases || !Array.isArray(cases)) return cases || [];
    
    // More robust filtering logic based on slice tag
    return cases.filter(c => {
      if (!c) return false;
      
      switch (tag) {
        case 'length:short':
          return (c.latency_ms || 0) < 200;
        case 'length:long':
          return (c.latency_ms || 0) >= 200;
        case 'confidence:high':
          return Boolean(c.pass_result);
        case 'result:fail':
          return !Boolean(c.pass_result);
        case 'speed:fast':
          return (c.latency_ms || 0) < 150;
        default:
          return true;
      }
    });
  };

  const getSliceColor = (delta) => {
    if (delta > 0.1) return 'success';
    if (delta < -0.1) return 'error';
    return 'warning';
  };

  const getSliceIcon = (name) => {
    if (name.includes('Short')) return <SpeedIcon sx={{ color: 'success.main' }} />;
    if (name.includes('Long')) return <MenuBookIcon sx={{ color: 'warning.main' }} />;
    if (name.includes('High')) return <GpsFixedIcon sx={{ color: 'info.main' }} />;
    if (name.includes('Failed')) return <CancelIcon sx={{ color: 'error.main' }} />;
    if (name.includes('Fast')) return <RocketIcon sx={{ color: 'primary.main' }} />;
    return <AnalyticsIcon sx={{ color: 'text.secondary' }} />;
  };

  return (
    <Box sx={{ p: 4 }}>
      {process.env.NODE_ENV !== 'production' && (
        <Typography variant="caption" sx={{ opacity: 0.4, position: 'absolute', top: 4, right: 8 }}>
          SliceCore debug: runs={runs.length} sel={selectedRunId || '∅'} slices={sliceData ? sliceData.slices.length : '∅'} loading={String(loading)}
        </Typography>
      )}
      <Card sx={{ p: 4, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <AnalyticsIcon sx={{ fontSize: 32, color: 'info.main' }} />
          <Typography variant="h5" gutterBottom>Slice</Typography>
        </Stack>
        <Typography variant="body2" sx={{ opacity: 0.8, mb: 3 }}>
          Discover performance patterns across different test case segments
        </Typography>

        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <FormControl sx={{ minWidth: 300 }} disabled={runsLoading}>
            <InputLabel>Completed Run</InputLabel>
            <Select 
              value={selectedRunId} 
              onChange={e => setSelectedRunId(e.target.value)} 
              label="Completed Run"
            >
              {runsLoading && (
                <MenuItem disabled>Loading runs...</MenuItem>
              )}
              {!runsLoading && runs.length === 0 && (
                <MenuItem disabled>No completed runs found</MenuItem>
              )}
              {runs.map((run) => {
                const passRate = formatPercent(run.metrics?.pass_rate);
                const passLabel = passRate === '—' ? '—' : `${passRate}%`;
                return (
                  <MenuItem key={run.id} value={run.id}>
                    {formatDateTime(run.created_at)} • Pass Rate: {passLabel}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <Button 
            variant="contained" 
            onClick={() => analyzeSlices(selectedRunId)}
            disabled={loading || !selectedRunId}
            size="large"
            startIcon={<SearchIcon />}

          >
            {loading ? 'Hunting...' : 'Hunt Slices'}
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      </Card>

      {sliceData && sliceData.slices && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
          {/* Slice Overview */}
          <Card sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <TrendingUpIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6">Performance Slices</Typography>
            </Stack>
            <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
              Click to filter cases by slice
            </Typography>
            
            <Stack spacing={2}>
              {sliceData.slices.map((slice, index) => (
                <Card 
                  key={index}
                  sx={{ 
                    p: 2, 
                    cursor: 'pointer',
                    border: selectedSlice === slice.tag ? 2 : 1,
                    borderColor: selectedSlice === slice.tag ? 'primary.main' : 'divider',
                    transition: 'all 0.2s ease',
                    '&:hover': { 
                      borderColor: 'primary.main',
                      transform: 'translateY(-2px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => setSelectedSlice(selectedSlice === slice.tag ? '' : slice.tag)}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {getSliceIcon(slice.name)}
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {slice.name || 'Unnamed Slice'}
                      </Typography>
                    </Stack>
                    <Chip 
                      label={`${((slice.pass_rate || 0) * 100).toFixed(1)}%`}
                      color={getSliceColor(slice.delta || 0)}
                      size="small"
                    />
                  </Stack>
                  
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {slice.count || 0} cases
                    </Typography>
                    <Chip 
                      icon={(slice.delta || 0) >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                      label={`${(slice.delta || 0) >= 0 ? '+' : ''}${((slice.delta || 0) * 100).toFixed(1)}% delta`}
                      color={getSliceColor(slice.delta || 0)}
                      variant="outlined"
                      size="small"
                    />
                  </Stack>
                </Card>
              ))}
              
              {sliceData.slices.length === 0 && (
                <Typography variant="body2" sx={{ opacity: 0.7, textAlign: 'center', py: 2 }}>
                  No significant slices found
                </Typography>
              )}
            </Stack>
          </Card>

          {/* Case Details */}
          <Card sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <ListIcon sx={{ color: 'info.main' }} />
              <Typography variant="h6">
                Test Cases {selectedSlice ? `(${selectedSlice})` : '(All)'}
              </Typography>
            </Stack>
            
            <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
              <List dense>
                {getFilteredCases(selectedSlice).map((case_, index) => (
                  <React.Fragment key={case_.id}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">
                              Case #{case_.idx || 'Unknown'}: {case_.notes || 'No notes'}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip 
                                icon={case_.pass_result ? <CheckIcon /> : <CancelIcon />}
                                label={case_.pass_result ? 'PASS' : 'FAIL'}
                                color={case_.pass_result ? 'success' : 'error'}
                                size="small"
                              />
                              <Chip
                                icon={<SpeedIcon />}
                                label={`${case_.latency_ms || 0}ms`}
                                size="small"
                                variant="outlined"
                              />
                            </Stack>
                          </Stack>
                        }
                        secondary={
                          case_.citations && case_.citations.length > 0 && (
                            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                              Citations: {case_.citations.join(', ')}
                            </Typography>
                          )
                        }
                      />
                    </ListItem>
                    {index < getFilteredCases(selectedSlice).length - 1 && <Divider />}
                  </React.Fragment>
                ))}
                
                {getFilteredCases(selectedSlice).length === 0 && (
                  <Typography variant="body2" sx={{ opacity: 0.7, textAlign: 'center', py: 4 }}>
                    No cases match the selected slice
                  </Typography>
                )}
              </List>
            </Box>
          </Card>
        </Box>
      )}
      {!sliceData && !loading && (
        <Card sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            No slice analysis yet. Select a completed run and click "Hunt Slices".
          </Typography>
        </Card>
      )}
    </Box>
  );
}
